import type { Building } from "building-types/building/buildingTypes";
import type { BuildingAnalyticEntity } from "building-types/buildingAnalyticEntityTypes";
import { buildingGeomAttributeTagNew, FLOOR_STRUCTURE } from "building-types/buildingGeomTag/buildingGeomTagTypes";
import { StructuralFloorType } from "building-types/floors/floorComponentsTypes";
import type { SolidFloor, SolidFramingFloor } from "building-types/floors/floorTypes";
import { FloorType } from "building-types/floors/floorTypes";
import { framingComponentTypeLabels } from "building-types/framing/framingComponentTypes";
import type {
  GeneratedFloorBaseAssembly,
  GeneratedFramedFloorAssembly,
  GeneratedSlabFloorAssembly,
} from "building-types/horizontalAssemblies/generatedFloorAssemblyTypes";
import { RoomClass, RoomCondition } from "building-types/rooms/roomTypesEnum";
import { RoomEnclosure } from "building-types/rooms/roomTypesEnum";
import type { RWTable, Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import {
  feetSquaredFromInchesSquared,
  inchesSquaredNew,
  percentNew,
  worldFeetFromWorldInches,
  worldInchesNew,
} from "common/utils/nominalNumbers";
import { defaultFloorAssemblyHashParamSet, isVoidFloorAssemblyOverride } from "doc/elements/floor/floorAssembly";
import {
  brepNew,
  faceCopy,
  faceNew,
  faceRepairLoopDirections,
  faceReverse,
  facesArea,
  faceTopoNew,
  type Face,
} from "geom/brep";
import { extrudePolyLine2sOnPlane } from "geom/extrude";
import { planeNewXY } from "geom/plane";
import type { DCEL } from "geom/polyclip/dcel";
import { dcelFromPolygonsSnapRound } from "geom/polyclip/dcelFromPolygons";
import { polygonArrayToPolyLine2Array, polyLine2ArrayToPolygon } from "geom/polyclip/polyClip";
import { halfEdgeLoopsToPolygons, intersectUnion, subtract, union } from "geom/polyclip/setOps";
import { polyLine2Length, polyLine2MergeCollinearEdges } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { nonZeroFloorThickness } from "../elements/floor/floor";
import { sortedLevels } from "../elements/level/levelTools";
import { makeFloorSystems, select as generateFloorSystemsSelect } from "../generation/generateFloorSystems";
import {
  select as generateRoomFloorAssembliesSelect,
  makeRoomClassFloorAssembliesAndGroups,
} from "../generation/generateRoomFloorAssemblies";
import { addAnalyticBRep } from "../utils/generateUtil";
import makeBuildingSelector from "../utils/makeBuildingSelector";
import { memoAsync } from "../utils/memo";
import { getColorForRoomConditionAdjacency } from "./floorConditionAdjacencyDebugColor";
import { processFloorBoundariesToGeometry } from "./generateFloorHorizontalAssembliesTools";

type FloorStructureInputs = Pick<
  Building,
  "levels" | "framedFloorAssemblies" | "slabFloorAssemblies" | "floorBoundaries" | "resolvedDatums"
> & {
  readonly doc: Pick<Building["doc"], "floors" | "floorSystems" | "frameFloorAssemblies" | "framingComponents">;
  readonly roomFloorAssemblyInputs: ReturnType<typeof generateRoomFloorAssembliesSelect>;
  readonly floorSystemInputs: ReturnType<typeof generateFloorSystemsSelect>;
};

interface GenericFloorBase {
  readonly levelId: string;
  /** the face properties represent the outline (including holes) and z offset of the floor */
  readonly face: Face;
}

/** An intermediate representation of solid floors and slabs */
interface GenericSolidFloor extends GenericFloorBase {
  readonly type: "SOLID";
  readonly roomClass: RoomClass;
  readonly roomEnclosure: RoomEnclosure;
  readonly structuralType: StructuralFloorType;
  readonly floorPlateThickness: number;
  readonly assemblyType?: string;
}

interface GenericVoidFloor extends GenericFloorBase {
  readonly type: "VOID";
}

type GenericFloor = GenericSolidFloor | GenericVoidFloor;

interface AnalyticFloorStructure extends GenericSolidFloor {
  readonly conditionAbove: RoomCondition;
  readonly conditionBelow: RoomCondition;
  readonly exteriorEdges: PolyLine2[];
}

/** TODO: refactor this to use the same source for floor areas as the ceiling analytic entities */
export async function addFloorStructureAnalyticEntities(
  building: FloorStructureInputs,
): Promise<Table<BuildingAnalyticEntity>> {
  const analyticEntities: RWTable<BuildingAnalyticEntity> = {};

  const { levels } = building;

  // TODO: update this to account for level stacking
  const sortedLevelsArray = sortedLevels(levels);

  const { conditionedFloors, unconditionedFloors, openToBelowFloors } =
    await makeAnalyticFloorStructureFromFloorSystemAndFoundationSuperSystem(building);

  let prevLevelId: string | undefined;
  for (const level of sortedLevelsArray) {
    const levelId = level.id;
    const currLevelConditionedFloors = conditionedFloors[levelId];
    const currLevelUnconditionedFloors = unconditionedFloors[levelId];
    const currLevelOpenToBelowFloors = openToBelowFloors[levelId];

    // we want to compare the current level's floors to only the conditioned floors of
    // the previous level (if there is a previous level)
    const prevLevelConditionedFloors = prevLevelId ? conditionedFloors[prevLevelId] : [];

    const combinedLevelFloors: GenericFloor[] = [];

    let currOperand = 0;
    const addGenericFloorStructures = (floors: GenericFloor[]): number[] => {
      combinedLevelFloors.push(...floors);
      return Array.from(floors, () => currOperand++);
    };

    const currLevelConditionedFloorsOperands: number[] = addGenericFloorStructures(currLevelConditionedFloors);
    const currLevelUnconditionedFloorsOperands: number[] = addGenericFloorStructures(currLevelUnconditionedFloors);
    const currLevelOpenToBelowFloorsOperands: number[] = addGenericFloorStructures(currLevelOpenToBelowFloors);
    const prevLevelConditionedFloorsOperands: number[] = addGenericFloorStructures(prevLevelConditionedFloors);

    const converted = polyLine2ArrayToPolygon(combinedLevelFloors.map((floor) => floor.face.loops));

    // the 1e-3 precision is necessary to handle foundation slab offsets
    const dcel = dcelFromPolygonsSnapRound({ polygons: converted, snapRoundPrecision: 1e-3 });

    if (!dcel) {
      continue;
    }

    const levelPerimeterHalfEdgeSet: Set<number> = new Set(
      union(
        dcel,
        [
          ...currLevelConditionedFloorsOperands,
          ...currLevelUnconditionedFloorsOperands,
          ...currLevelOpenToBelowFloorsOperands,
        ],
        emitHalfEdgeLoops,
      ).flat(),
    );

    const polyOpFloors = (
      operands: number[],
      op: "intersect" | "subtract",
      conditionAbove: RoomCondition,
      conditionBelow: RoomCondition,
    ) => {
      polyOpAnalyticFloorStructures(
        analyticEntities,
        dcel,
        prevLevelConditionedFloorsOperands,
        operands,
        combinedLevelFloors,
        op,
        conditionAbove,
        conditionBelow,
        levelPerimeterHalfEdgeSet,
      );
    };

    polyOpFloors(
      [...currLevelConditionedFloorsOperands],
      "subtract",
      RoomCondition.CONDITIONED,
      RoomCondition.UNCONDITIONED,
    );
    polyOpFloors(
      [...currLevelUnconditionedFloorsOperands],
      "subtract",
      RoomCondition.UNCONDITIONED,
      RoomCondition.UNCONDITIONED,
    );

    if (prevLevelId) {
      polyOpFloors(
        currLevelUnconditionedFloorsOperands,
        "intersect",
        RoomCondition.UNCONDITIONED,
        RoomCondition.CONDITIONED,
      );
      polyOpFloors(
        currLevelConditionedFloorsOperands,
        "intersect",
        RoomCondition.CONDITIONED,
        RoomCondition.CONDITIONED,
      );
    }

    prevLevelId = levelId;
  }

  addHorizontalAssemblyFloorAnalyticEntities(building, analyticEntities);

  return analyticEntities;
}

function addHorizontalAssemblyFloorAnalyticEntities(
  building: FloorStructureInputs,
  analyticEntities: RWTable<BuildingAnalyticEntity>,
): void {
  const { levels } = building;

  type FloorStructureAssembly =
    | {
        readonly id: string;
        readonly assembly: GeneratedFramedFloorAssembly;
        readonly structuralType: StructuralFloorType.FRAMING;
      }
    | {
        readonly id: string;
        readonly assembly: GeneratedSlabFloorAssembly;
        readonly structuralType: StructuralFloorType.SLAB;
      };

  const assemblies: FloorStructureAssembly[] = [];

  for (const id in building.framedFloorAssemblies) {
    assemblies.push({
      id,
      assembly: building.framedFloorAssemblies[id],
      structuralType: StructuralFloorType.FRAMING,
    });
  }
  for (const id in building.slabFloorAssemblies) {
    assemblies.push({
      id,
      assembly: building.slabFloorAssemblies[id],
      structuralType: StructuralFloorType.SLAB,
    });
  }

  for (const { id, assembly, structuralType } of assemblies) {
    const boundaries = building.floorBoundaries[id];
    if (!boundaries?.perimeters.length) {
      continue;
    }

    const floor = building.doc.floors[assembly.parentFloorId];
    if (!floor || isVoidFloorAssemblyOverride(floor)) {
      continue;
    }

    let geometry;
    try {
      geometry = processFloorBoundariesToGeometry(boundaries, floor, building.resolvedDatums, building.levels);
    } catch {
      continue;
    }

    const { levelId } = assembly;
    const level = levels[levelId];
    const floorElevation = level?.floorElevation ?? 0;

    const floorPlateThickness = getGeneratedFloorPlateThickness(assembly, structuralType);
    const assemblyType =
      structuralType === StructuralFloorType.FRAMING
        ? getGeneratedFramedFloorAssemblyType(building, assembly)
        : undefined;

    for (const island of geometry.islands) {
      const loops = [island.perimeter, ...island.openings];
      const face = faceRepairLoopDirections(faceNew(loops, planeNewXY(0, 0, floorElevation)));
      const exteriorPerimeterLength = polyLine2Length(island.perimeter);
      const exteriorPerimeterFaceArea = facesArea(
        extrudePolyLine2sOnPlane(face.plane, [island.perimeter], -floorPlateThickness).flat().map(faceReverse),
      );

      const faceTopos = [faceTopoNew()];
      const brep = brepNew([face], faceTopos);
      const parents = [
        { type: "LEVEL" as const, id: levelId },
        { type: "FLOOR_SYSTEM" as const, id: floor.parentId },
        { type: "FLOOR" as const, id: assembly.parentFloorId },
        ...(assembly.bayId ? [{ type: "FLOOR_BAY" as const, id: assembly.bayId }] : []),
      ];

      addAnalyticBRep(
        "ANALYTIC_FLOOR_STRUCTURE",
        analyticEntities,
        "Floor Structure",
        parents,
        brep,
        undefined,
        undefined,
        undefined,
        [
          FLOOR_STRUCTURE,
          buildingGeomAttributeTagNew("structuralType", structuralType, "STRING"),
          ...(assemblyType ? [buildingGeomAttributeTagNew("assemblyType", assemblyType, "STRING")] : []),
          buildingGeomAttributeTagNew("floorPlateThickness", worldInchesNew(floorPlateThickness), "INCHES"),
          buildingGeomAttributeTagNew(
            "exteriorPerimeterLength",
            worldFeetFromWorldInches(worldInchesNew(exteriorPerimeterLength)),
            "FEET",
          ),
          buildingGeomAttributeTagNew(
            "exteriorPerimeterFaceArea",
            feetSquaredFromInchesSquared(inchesSquaredNew(exteriorPerimeterFaceArea)),
            "FEET_SQUARED",
          ),
        ],
        {
          color: 0x808080,
          opacity: percentNew(0.5),
          lineWidth3d: worldInchesNew(2),
        },
        undefined,
      );
    }
  }
}

function getGeneratedFloorPlateThickness(
  assembly: GeneratedFloorBaseAssembly,
  structuralType: StructuralFloorType,
): number {
  const subfloorThickness = Math.abs(assembly.subfloorThickness ?? assembly.subfloor[0]?.dirZ ?? 0);
  if (structuralType === StructuralFloorType.SLAB) {
    return subfloorThickness + Math.abs((assembly as GeneratedSlabFloorAssembly).slab[0]?.dirZ ?? 0);
  }
  return subfloorThickness + getGeneratedFramedFloorThickness(assembly as GeneratedFramedFloorAssembly);
}

function getGeneratedFramedFloorThickness(assembly: GeneratedFramedFloorAssembly): number {
  let framingThickness = 0;
  for (const members of assembly.framing) {
    for (const member of members) {
      framingThickness = Math.max(framingThickness, member.profileDescriptor.width);
    }
  }
  return framingThickness;
}

export function getGeneratedFramedFloorAssemblyType(
  building: Pick<FloorStructureInputs, "doc" | "levels">,
  assembly: GeneratedFramedFloorAssembly,
): string | undefined {
  const floor = building.doc.floors[assembly.parentFloorId];
  const floorSystemAssemblyId = floor ? building.doc.floorSystems[floor.parentId]?.assemblyId : undefined;
  const levelAssemblyId = building.levels[assembly.levelId]?.floorAssemblyId;
  const inheritedAssemblyId = floorSystemAssemblyId ?? levelAssemblyId;
  const bayAssemblyId = assembly.bayId && floor ? floor.splitData?.bays[assembly.bayId]?.assemblyId : undefined;
  const assemblyId = bayAssemblyId ?? inheritedAssemblyId;
  const frameFloorAssembly = assemblyId ? building.doc.frameFloorAssemblies[assemblyId] : undefined;
  if (frameFloorAssembly) {
    const componentType = building.doc.framingComponents[frameFloorAssembly.joistComponentId]?.type;
    return componentType ? framingComponentTypeLabels[componentType] : undefined;
  }
  return undefined;
}

const polyOpAnalyticFloorStructures = (
  analyticEntities: RWTable<BuildingAnalyticEntity>,
  dcel: DCEL,
  prevLevelConditionedFloorsOperands: number[],
  operands: number[],
  floors: GenericFloor[],
  op: "intersect" | "subtract",
  conditionAbove: RoomCondition,
  conditionBelow: RoomCondition,
  levelPerimeterHalfEdgeSet: Set<number>,
): void => {
  const opFn =
    op === "intersect"
      ? (polygonId: number) => intersectUnion(dcel, polygonId, prevLevelConditionedFloorsOperands, emitHalfEdgeLoops)
      : (polygonId: number) => subtract(dcel, [polygonId], prevLevelConditionedFloorsOperands, emitHalfEdgeLoops);

  for (const currFloorOperand of operands) {
    const floor = floors[currFloorOperand];
    if (!floor || floor.type === "VOID") {
      continue;
    }

    const halfEdgeLoops = opFn(currFloorOperand);
    const polygons = halfEdgeLoopsToPolygons(dcel, halfEdgeLoops);
    const polyLine2s = polygonArrayToPolyLine2Array(polygons, false, false).map((polygon) =>
      polygon.map((pl) => polyLine2MergeCollinearEdges(pl)),
    );

    for (let i = 0; i < polyLine2s.length; i++) {
      const polyLine2 = polyLine2s[i];
      const face = faceRepairLoopDirections(faceNew(polyLine2, floor.face.plane));

      const halfEdgeLoop = halfEdgeLoops[i];
      const exteriorEdges: PolyLine2[] = [];

      for (let j = 0; j < halfEdgeLoop.length; j++) {
        const halfEdge = halfEdgeLoop[j];
        if (levelPerimeterHalfEdgeSet.has(halfEdge)) {
          const [sX, sY] = dcel.vertices[dcel.halfEdges[halfEdge][0]];
          const [eX, eY] = dcel.vertices[dcel.halfEdges[halfEdge][1]];
          exteriorEdges.push(polyLine2New([sX, sY, eX, eY]));
        }
      }

      const floorStructure = makeAnalyticFloorStructureWithConditions(
        floor,
        face,
        exteriorEdges,
        conditionAbove,
        conditionBelow,
      );

      addAnalyticFloorStructure(analyticEntities, floorStructure);
    }
  }
};

function addAnalyticFloorStructure(
  analyticEntities: RWTable<BuildingAnalyticEntity>,
  analyticFloor: AnalyticFloorStructure,
) {
  const {
    conditionAbove,
    conditionBelow,
    exteriorEdges,
    face,
    floorPlateThickness,
    levelId,
    roomClass,
    structuralType,
    assemblyType,
  } = analyticFloor;

  const color = getColorForRoomConditionAdjacency(conditionAbove, conditionBelow);

  // exterior edge faces
  const exteriorEdgeFaces = extrudePolyLine2sOnPlane(face.plane, exteriorEdges, -floorPlateThickness)
    .flat()
    .map(faceReverse);

  const exteriorPerimeterLength = exteriorEdges.reduce((acc, edge) => acc + polyLine2Length(edge), 0);
  const exteriorPerimeterArea = facesArea(exteriorEdgeFaces);

  const faces = [face, ...exteriorEdgeFaces];
  const faceTopos = faces.map(faceTopoNew);
  const brep = brepNew(faces, faceTopos);

  addAnalyticBRep(
    "ANALYTIC_FLOOR_STRUCTURE",
    analyticEntities,
    "Floor Structure",
    [{ type: "LEVEL", id: levelId }],
    brep,
    undefined,
    undefined,
    undefined,
    [
      FLOOR_STRUCTURE,
      buildingGeomAttributeTagNew("conditionAbove", conditionAbove, "STRING"),
      buildingGeomAttributeTagNew("conditionBelow", conditionBelow, "STRING"),
      buildingGeomAttributeTagNew("roomClass", RoomClass[roomClass], "STRING"),
      buildingGeomAttributeTagNew("structuralType", structuralType, "STRING"),
      ...(assemblyType ? [buildingGeomAttributeTagNew("assemblyType", assemblyType, "STRING")] : []),
      buildingGeomAttributeTagNew("floorPlateThickness", worldInchesNew(floorPlateThickness), "INCHES"),
      buildingGeomAttributeTagNew(
        "exteriorPerimeterLength",
        worldFeetFromWorldInches(worldInchesNew(exteriorPerimeterLength)),
        "FEET",
      ),
      buildingGeomAttributeTagNew(
        "exteriorPerimeterFaceArea",
        feetSquaredFromInchesSquared(inchesSquaredNew(exteriorPerimeterArea)),
        "FEET_SQUARED",
      ),
    ],
    {
      color,
      opacity: percentNew(0.5),
      lineWidth3d: worldInchesNew(2),
    },
  );
}

function makeGenericSolidFloor(
  building: FloorStructureInputs,
  floor: SolidFloor,
  levelId: string,
  face: Face,
  roomEnclosure: RoomEnclosure,
): GenericSolidFloor {
  return {
    type: "SOLID",
    roomClass: floor.roomClass,
    roomEnclosure,
    structuralType: floor.structuralType,
    floorPlateThickness: floor.floorPlateThickness,
    assemblyType:
      floor.structuralType === StructuralFloorType.FRAMING
        ? getLegacyFramedFloorAssemblyType(building, floor)
        : undefined,
    levelId: levelId,
    face,
  };
}

function getLegacyFramedFloorAssemblyType(
  building: FloorStructureInputs,
  floor: SolidFramingFloor,
): string | undefined {
  const assemblyId = floor.hashParams.assemblyId;
  const frameFloorAssembly = assemblyId ? building.doc.frameFloorAssemblies[assemblyId] : undefined;
  if (frameFloorAssembly) {
    const componentType = building.doc.framingComponents[frameFloorAssembly.joistComponentId]?.type;
    return componentType ? framingComponentTypeLabels[componentType] : undefined;
  }
  return undefined;
}

function makeGenericVoidFloor(levelId: string, face: Face): GenericVoidFloor {
  return {
    type: "VOID",
    levelId: levelId,
    face,
  };
}
function makeAnalyticFloorStructureWithConditions(
  floorProps: Omit<GenericSolidFloor, "face">,
  face: Face,
  exteriorEdges: PolyLine2[],
  conditionAbove: RoomCondition,
  conditionBelow: RoomCondition,
): AnalyticFloorStructure {
  return {
    ...floorProps,
    face,
    exteriorEdges,
    conditionAbove,
    conditionBelow,
  };
}

function emitHalfEdgeLoops(_dcel: DCEL, halfEdgeLoops: number[][]): number[][] {
  return halfEdgeLoops;
}

/**
 * This is the main function for generating the floor systems using a different hash for the floor assemblies.
 * TODO: remove generation of the foundation super systems once they are generated inside floorSystems.
 */
async function makeAnalyticFloorStructureFromFloorSystemAndFoundationSuperSystem(
  building: FloorStructureInputs,
): Promise<{
  readonly conditionedFloors: Table<GenericSolidFloor[]>;
  readonly unconditionedFloors: Table<GenericSolidFloor[]>;
  readonly openToBelowFloors: Table<GenericVoidFloor[]>;
}> {
  const { roomFloorAssemblies, roomClassFloorAssemblyGroups } = makeRoomClassFloorAssembliesAndGroups(
    building.roomFloorAssemblyInputs,
    new Set([...defaultFloorAssemblyHashParamSet, "openToBelow", "roomEnclosure"]),
  );

  const { floorSystems, foundationSuperSystems } = await makeFloorSystems({
    ...building.floorSystemInputs,
    roomFloorAssemblies,
    roomClassFloorAssemblyGroups,
  });

  const conditionedFloors: RWTable<GenericSolidFloor[]> = {};
  const unconditionedFloors: RWTable<GenericSolidFloor[]> = {};
  const openToBelowFloors: RWTable<GenericVoidFloor[]> = {};

  for (const levelId in building.levels) {
    const levelFloors: SolidFloor[] = floorSystems[levelId].roomClassFloors
      .flat()
      .filter((floor) => floor.floorType !== FloorType.NONE)
      .filter((floor) => nonZeroFloorThickness(floor.floorPlateThickness));

    conditionedFloors[levelId] = [];
    unconditionedFloors[levelId] = [];
    openToBelowFloors[levelId] = [];

    for (const floor of levelFloors) {
      const {
        floorPlate,
        hashParams: { roomEnclosure, openToBelow },
      } = floor;

      assertIsNotUndefined(roomEnclosure);
      assertIsNotUndefined(openToBelow);

      for (const face of floorPlate.perimeterFaces) {
        if (openToBelow) {
          openToBelowFloors[levelId].push(makeGenericVoidFloor(levelId, face));
        } else {
          const analyticFloorStructure = makeGenericSolidFloor(building, floor, levelId, face, roomEnclosure);
          roomEnclosure === RoomEnclosure.CONDITIONED
            ? conditionedFloors[levelId].push(analyticFloorStructure)
            : unconditionedFloors[levelId].push(analyticFloorStructure);
        }
      }
    }
  }

  for (const foundationSuperSystemId in foundationSuperSystems) {
    const foundationSuperSystem = foundationSuperSystems[foundationSuperSystemId];
    for (const slab of foundationSuperSystem.superSystem.slabs) {
      const {
        hashParams: { roomEnclosure },
      } = slab;

      assertIsNotUndefined(roomEnclosure);

      const analyticFloorStructure: GenericSolidFloor = {
        type: "SOLID",
        roomClass: slab.roomClass,
        roomEnclosure,
        structuralType: StructuralFloorType.SLAB,
        floorPlateThickness: Math.abs(slab.extrusion.dirZ),
        levelId: foundationSuperSystem.levelId,
        face: faceReverse(faceCopy(slab.extrusion.profile)),
      };

      roomEnclosure === RoomEnclosure.CONDITIONED
        ? conditionedFloors[foundationSuperSystem.levelId].push(analyticFloorStructure)
        : unconditionedFloors[foundationSuperSystem.levelId].push(analyticFloorStructure);
    }
  }

  return { conditionedFloors, unconditionedFloors, openToBelowFloors };
}

export const select = makeBuildingSelector([
  "levels",
  "framedFloorAssemblies",
  "slabFloorAssemblies",
  "floorBoundaries",
  "resolvedDatums",
  "doc",
  "optionChoices",
  "entityDisabledByGroup",
  "rooms",
  "wallMasses",
  "walls",
  "settings",
  "settingsSets",
  "casework",
  "props",
  "roomsByLevel",
  "roomOutlineTable",
  "roomLevelIndices",
  "roomFloorAssemblies",
  "roomFloorFinishes",
  "roomClassFloorAssemblyGroups",
  "planarGraphs",
  "planarGraphLevelAssociations",
  "interiorPalette",
  "exteriorPalette",
  "floorPaths",
  "foundationPaths",
]);

export async function generate(
  building: ReturnType<typeof select>,
): Promise<Pick<Building, "analyticFloorStructureEntities">> {
  return {
    analyticFloorStructureEntities: await addFloorStructureAnalyticEntities({
      ...building,
      roomFloorAssemblyInputs: generateRoomFloorAssembliesSelect(building),
      floorSystemInputs: generateFloorSystemsSelect(building),
    }),
  };
}

export default memoAsync(select, generate);
